import * as React from "react";
import {
  boundedSiteIds,
  isUnbounded,
  ROLE_REACH_LABELS,
  type Role,
  type Scope,
  type SiteSummary,
} from "@taskiq/shared";
import { setActiveScope, useActiveScope } from "./active-scope";
import { useAuth } from "./auth-context";

/**
 * Who the signed-in user is *tenancy-wise*, as questions a component can ask.
 *
 * **No component should compare against `"superadmin"`.** That is the entire
 * reason this file exists. A Visionify staffer's console differs from a
 * customer's in a handful of specific ways - they must pick a company where
 * others have one implicitly, they see a reach label instead of a company name,
 * they have no site assignments - and each of those is a property with a name
 * here rather than a role check scattered through the pages that need it.
 *
 * Two reasons that matters beyond tidiness. A fourth role breaks every `role ===`
 * comparison silently, by rendering the *wrong* UI rather than failing to
 * compile. And the console is not a security boundary in any case: hiding a
 * button is a courtesy, and RLS is what actually refuses the write. Branching on
 * a role here invites the belief that the branch is protecting something.
 */
export interface Tenancy {
  /** Nothing bounds this user. They work across every workspace and every location. */
  unbounded: boolean;

  /**
   * The company every screen should default to and every write should be pinned
   * to. Null when unbounded - which means "they must choose", not "no access".
   */
  companyId: string | null;

  /** The sites bounding this user, or null when they are not bounded by site. */
  siteIds: readonly string[] | null;

  /** Their default company. Always present: every user has a home. */
  homeCompany: { id: string; name: string; slug: string } | null;

  /** Their default site - the one badged "Default", where sign-in lands them. */
  homeSite: SiteSummary | null;

  /**
   * True when a form has to ask which company it is acting on.
   *
   * The inverse is not "they have one company" but "the API will pin it for
   * them and ignore anything we send" - so a picker would be a control with no
   * effect, which is worse than no control.
   */
  mustChooseCompany: boolean;

  /** True for the user's own default site. Drives the "Default" pill. */
  isDefaultSite: (siteId: string) => boolean;

  /**
   * The site the console is working in. Site-scoped paths are built from it -
   * `/sites/${activeSiteId}/users`.
   *
   * Null only before the session resolves. There is no all-sites value: the
   * console is one site at a time, everywhere.
   */
  activeSiteId: string | null;

  /** The company the switcher is pointed at. Which sites are offered follows it. */
  activeCompanyId: string | null;

  /** True when this user has more than one company to point the switcher at. */
  canSwitchCompany: boolean;

  /** Move the console to another site, and to the company holding it. */
  setActiveScope: (scope: { companyId: string | null; siteId: string }) => void;

  /** A short phrase for the topbar and sidebar: what this user can currently see. */
  reachLabel: string;

  /**
   * The same fact as a full sentence, for the "your access" card.
   *
   * Two forms rather than one because the topbar has room for two words and the
   * access card is answering "why can't I see X" - the most common support
   * question a scoped console generates, and one that deserves a whole sentence.
   */
  reachSentence: string;
}

export function useTenancy(): Tenancy {
  const { user, scope } = useAuth();
  const active = useActiveScope();

  return React.useMemo<Tenancy>(() => {
    if (!scope || !user) {
      return {
        unbounded: false,
        companyId: null,
        siteIds: [],
        homeCompany: null,
        homeSite: null,
        mustChooseCompany: false,
        isDefaultSite: () => false,
        activeSiteId: null,
        activeCompanyId: null,
        canSwitchCompany: false,
        setActiveScope,
        reachLabel: "",
        reachSentence: "",
      };
    }

    const unbounded = isUnbounded(scope);

    return {
      unbounded,
      companyId: scope.companyId,
      siteIds: boundedSiteIds(scope),
      homeCompany: user.company,
      homeSite: user.site,
      mustChooseCompany: unbounded,
      isDefaultSite: (siteId) => siteId === user.site.id,
      activeSiteId: active.siteId ?? user.site.id,
      activeCompanyId: active.companyId ?? user.company.id,
      // Not `role === "superadmin"`. The question the switcher is asking is
      // "is there a second company to point at", and the answer is a property
      // of the reach: a caller whose scope pins a company has exactly one.
      canSwitchCompany: unbounded,
      setActiveScope,
      reachLabel: describeReach(scope, user.role, user.company.name, user.site.name),
      reachSentence: describeReachInFull(scope, user.role, user.company.name, user.site.name),
    };
  }, [scope, user, active]);
}

/**
 * What this user can see, in three or four words for the topbar.
 *
 * Names the actual company or site wherever one bounds them, because "Every site
 * in their company" is a sentence about a role and "Acme Utilities" is a fact
 * about the person reading it.
 */
function describeReach(scope: Scope, role: Role, companyName: string, siteName: string): string {
  if (isUnbounded(scope)) return ROLE_REACH_LABELS[role];

  const siteIds = boundedSiteIds(scope);
  if (siteIds === null) return companyName;

  if (siteIds.length <= 1) return siteName;
  return `${siteIds.length} locations`;
}

/**
 * The same fact, as a sentence.
 *
 * The zero case is real and worth naming: a field officer whose last assignment
 * was revoked still has a default site in the database, so saying "North Depot
 * only" would be a flat contradiction of the empty list they are looking at.
 */
function describeReachInFull(
  scope: Scope,
  role: Role,
  companyName: string,
  siteName: string
): string {
  if (isUnbounded(scope)) return ROLE_REACH_LABELS[role];

  const siteIds = boundedSiteIds(scope);
  if (siteIds === null) return `Every location in ${companyName}`;

  if (siteIds.length === 0) return "No locations assigned yet";
  if (siteIds.length === 1) return `${siteName} only`;
  return `${siteIds.length} assigned locations, defaulting to ${siteName}`;
}

/**
 * Whether granting a site to someone with this role actually changes what they
 * can reach.
 *
 * Re-exported so the site-assignment UI can say so plainly. An admin already
 * reaches every site in their company, so their checkboxes are a record rather
 * than a grant - and a checkbox that silently does nothing is a worse lie than
 * a sentence explaining why.
 */
